import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import ActionButton from '../../Components/Common/ActionButton';
import DeleteModal from '../../Components/Common/DeleteModal';
import ListPager from '../../Components/Common/ListPager';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListSearch,
} from '../../Components/Common/ListFilters';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import notify, { MESSAGES } from '../../utils/notify';
import '../TenderOpening/components/TenderOpening.css';
import axios from 'axios';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';
import { FormGrid, TextAreaField, TextField } from '../../Components/Form';

const LawList = () => {
  const token = getAuthToken();

  const [data, setData] = useState({ items: [] });
  const [deleteModal, setDeleteModal] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalLaw, setModalLaw] = useState(false);
  const [lawFormData, setLawFormData] = useState({
    name: null,
    code: null,
    description: null,
    id: null,
  });

  const [filterText, setFilterText] = useState('');
  const [search, setSearch] = useState('');
  const requestRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const handleDeleteModal = id => {
    const selectedRow = data.items.find(item => item.id === id);
    setInvoice(selectedRow);
    setDeleteModal(true);
  };

  const handleDeleteInvoice = async () => {
    if (invoice) {
      try {
        const response = await fetch(`${api.API_URL}/law/${invoice.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }

        notify.success('Law deleted successfully.');
        setDeleteModal(false);
        setInvoice(null);
        fetchData(currentPage, rowsPerPage, token, search);
      } catch (err) {
        notify.apiError(err, 'delete law');
      }
    }
  };

  const columns = [
    {
      id: 'name',
      name: 'Law Name',
      selector: row => row.name,
      sortable: true,
      width: '25%',
      cell: row => (
        <span className='to-title' title={String(row.name ?? '')}>
          {row.name ?? '-'}
        </span>
      ),
    },

    {
      id: 'code',
      name: 'Law Code',
      selector: row => row.code,
      sortable: true,
      width: '20%',
      cell: row => (
        <span className='pv-desc' title={String(row.code ?? '')}>
          {row.code ?? '-'}
        </span>
      ),
    },

    {
      id: 'description',
      name: 'Law Description',
      selector: row => row.description,
      sortable: true,
      width: '40%',
      cell: row => (
        <span className='pv-desc' title={String(row.description ?? '')}>
          {row.description ?? '-'}
        </span>
      ),
    },

    {
      name: <span className='text-center w-100'>Action</span>,
      width: '15%',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit'
            onClick={() => handleUpdateModal(row.id)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            danger
            icon='ri-delete-bin-line'
            label='Delete'
            confirm={false}
            onClick={() => handleDeleteModal(row.id)}
          />
        </div>
      ),
    },
  ];

  document.title = 'Data Law';

  const handleChange = e => {
    const { name, value } = e.target;
    setLawFormData({ ...lawFormData, [name]: value });
  };

  // Debounce typing so each keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(filterText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filterText]);

  useEffect(() => {
    fetchData(currentPage, rowsPerPage, token, search);
  }, [currentPage, rowsPerPage, token, search]);

  const handleFilterChange = e => {
    setFilterText(e.target.value);
    // Clearing applies at once; typing waits for the debounce.
    if (!e.target.value) {
      setSearch('');
      setCurrentPage(1);
    }
  };

  const clearFilter = () => {
    setFilterText('');
    setSearch('');
    setCurrentPage(1);
  };

  const fetchData = async (currentPage, rowsPerPage, token, search) => {
    const reqId = ++requestRef.current;
    setLoading(true);
    try {
      const response = await fetch(
        `${api.API_URL}/law?PageNumber=${currentPage}&PageSize=${rowsPerPage}${search ? `&Search=${encodeURIComponent(search)}` : ''}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'same-origin',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (reqId !== requestRef.current) return; // stale response

      // Handle different possible response structures
      const items =
        result?.data?.items || result?.items || result?.data || result || [];
      const totalItems =
        result?.data?.totalItems || result?.totalItems || items.length;

      setData({
        items: Array.isArray(items) ? items : [],
      });
      setTotalRows(totalItems);
    } catch (error) {
      if (reqId !== requestRef.current) return;
      notify.apiError(error, 'load data');
      setData({ items: [] });
      setTotalRows(0);
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  };

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const handleNewModal = () => {
    setLawFormData({
      name: null,
      code: null,
      description: null,
      id: null,
    });
    lawModal();
  };

  const handleUpdateModal = async (mid = null) => {
    await fetchLawDetail(mid);
    lawModal();
  };

  const lawModal = () => {
    setModalLaw(!modalLaw);
  };

  const selectedFieldsLaw = {
    name: lawFormData.name,
    code: lawFormData.code,
    description: lawFormData.description,
  };

  const handleAddDataLaw = async () => {
    try {
      await axios.post(`${api.API_URL}/law`, selectedFieldsLaw, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      notify.success('Law added successfully.');
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search);
    }
  };

  const fetchLawDetail = async mid => {
    try {
      const response = await axios.get(`${api.API_URL}/law/${mid}`, {
        withXSRFToken: true,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: `application/json`,
        },
      });
      const data = response?.data?.data ?? response?.data ?? response;
      setLawFormData({
        name: data.name || null,
        code: data.code || null,
        description: data.description || null,
        id: data.id || null,
      });
    } catch (error) {
      // Handle error silently or show appropriate message
      notify.apiError(error, 'fetch law details');
    }
  };

  const handleUpdDataLaw = async mid => {
    try {
      await axios.put(`${api.API_URL}/law/${mid}`, selectedFieldsLaw, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      notify.success(MESSAGES.updated);
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search);
    }
  };

  const filterChips = [
    filterText && {
      key: 'search',
      label: 'Search',
      value: filterText,
      onRemove: () => handleFilterChange({ target: { value: '' } }),
    },
  ].filter(Boolean);

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => handleDeleteInvoice()}
          onCloseClick={() => setDeleteModal(false)}
          title='Delete this law?'
          message='It will be permanently deleted. This cannot be undone.'
          subject={invoice?.name}
          confirmLabel='Delete law'
        />
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='to-hero mb-4'>
                  <PageBreadcrumb
                    fallback={{ label: 'Law', to: '/apps-law-view' }}
                  />

                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='to-hero-icon'>
                      <i className='ri-scales-2-line' aria-hidden='true' />
                    </span>
                    <div className='flex-grow-1'>
                      <h4 className='to-hero-title'>Law</h4>
                      <p className='to-hero-subtitle'>
                        Maintain the laws referenced when preparing
                        procurements.
                      </p>
                    </div>
                    <div className='d-flex gap-2 flex-wrap'>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={() =>
                          fetchData(currentPage, rowsPerPage, token, search)
                        }
                      >
                        <i className='ri-refresh-line' aria-hidden='true' />
                        Refresh
                      </button>
                      <button
                        type='button'
                        className='to-hero-btn to-hero-btn--solid'
                        onClick={() => handleNewModal()}
                      >
                        <i className='ri-add-line' aria-hidden='true' />
                        Create New Law
                      </button>
                    </div>
                  </div>
                </div>

                <div className='to-panel'>
                  <div className='to-toolbar'>
                    <ListSearch
                      value={filterText}
                      onChange={handleFilterChange}
                      placeholder='Search laws...'
                      ariaLabel='Search laws'
                    />
                    <ClearFiltersButton
                      count={filterChips.length}
                      onClick={clearFilter}
                    />
                    <span className='to-toolbar-meta'>
                      {loading
                        ? 'Loading...'
                        : `Showing ${startEntry}–${endEntry} of ${totalRows} laws`}
                    </span>
                  </div>

                  <ActiveFilterChips chips={filterChips} />

                  <div className='to-table-wrap'>
                    <DataTable
                      columns={columns}
                      data={data.items}
                      highlightOnHover
                      persistTableHead
                      defaultSortFieldId='name'
                      defaultSortAsc
                      progressPending={loading}
                      progressComponent={<SkeletonRows />}
                      noDataComponent={
                        <EmptyState
                          title='No law found'
                          emptyHint='No laws have been registered yet. Laws added here become selectable in procurement documents.'
                          filteredHint={`No law matches "${filterText}".`}
                          hasFilters={Boolean(filterText)}
                          onClear={clearFilter}
                          action={{
                            label: 'Create New Law',
                            onClick: handleNewModal,
                          }}
                        />
                      }
                      customStyles={{
                        headCells: {
                          style: { paddingLeft: '12px', paddingRight: '12px' },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                          },
                        },
                        table: { style: { width: '100%' } },
                      }}
                    />
                  </div>

                  <ListPager
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    isLoading={loading}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePerRowsChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <Modal
            isOpen={modalLaw}
            role='dialog'
            autoFocus={true}
            centered
            id='addLawModal'
            toggle={lawModal}
            data-bs-backdrop='static'
            size='md'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => {
                setModalLaw(!modalLaw);
              }}
            >
              <span className='modal-title text-info fw-bold'>
                {lawFormData.id !== null ? 'Edit Law' : 'Add Law'}
              </span>
            </ModalHeader>

            <ModalBody>
              <FormGrid columns={1}>
                <TextField
                  label='Law name'
                  name='name'
                  placeholder='e.g. Undang-Undang Migas'
                  value={lawFormData.name}
                  onChange={handleChange}
                />
                <TextField
                  label='Law code'
                  name='code'
                  placeholder='e.g. UU-22-2001'
                  value={lawFormData.code}
                  onChange={handleChange}
                />
                <TextAreaField
                  label='Description'
                  name='description'
                  rows={5}
                  value={lawFormData.description}
                  onChange={handleChange}
                />
              </FormGrid>
            </ModalBody>

            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={() => {
                  setModalLaw(!modalLaw);
                }}
              >
                Close
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-save-line'
                onClick={async () => {
                  await (lawFormData.id !== null
                    ? handleUpdDataLaw(lawFormData.id)
                    : handleAddDataLaw());
                  setModalLaw(false);
                }}
              >
                {lawFormData.id !== null ? 'Update' : 'Save'}
              </ActionButton>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default LawList;
